import type { Constant } from '@pbt/pbt-ui-components'
import { Utils } from '@pbt/pbt-ui-components'

import { parseLabTestIdentifier } from '~/components/common/lab-orders/labTestUtils'
import { LabOrdersStatus, orderHasResults } from '~/constants/SOAPStates'

const LAB_TEST_DASHBOARD_PATH_REGEX = /^\/lab-tests-dashboard\/([^?]*)\/?$/

export interface OrderResultsDialogParams {
  canComplete: boolean
  hasResults: boolean
  orderId: string
  pdfUrl: string
  vendorId: string
}

/**
 * Parse a lab tests dashboard href into identifier and query params.
 * Returns null if href is not a valid lab-tests-dashboard path.
 */
export function parseLabTestDashboardUrl(
  href: string,
): { labTestIdentifier: string; queryParams: URLSearchParams } | null {
  if (!href.startsWith('/lab-tests-dashboard/')) {
    return null
  }
  const qIndex = href.indexOf('?')
  const pathname = qIndex >= 0 ? href.slice(0, qIndex) : href
  const search = qIndex >= 0 ? href.slice(qIndex + 1) : ''
  const match = pathname.match(LAB_TEST_DASHBOARD_PATH_REGEX)
  if (!match) {
    return null
  }
  let labTestIdentifier = match[1]
  if (!labTestIdentifier) {
    return null
  }
  labTestIdentifier = labTestIdentifier.replace(/\/$/, '')
  if (!labTestIdentifier) {
    return null
  }
  if (labTestIdentifier.includes('/')) {
    return null
  }
  const queryParams = new URLSearchParams(search)
  return { labTestIdentifier, queryParams }
}

/**
 * Build OrderResultsDialog props from parsed URL parts and lab order status list.
 * Returns null if vendorId, orderId, or pdfUrl are missing (e.g. non order-based identifier).
 */
export function getOrderResultsDialogParams(
  labTestIdentifier: string,
  queryParams: URLSearchParams,
  labOrderStatuses: Constant[],
): OrderResultsDialogParams | null {
  let parsed: ReturnType<typeof parseLabTestIdentifier>
  try {
    parsed = parseLabTestIdentifier(labTestIdentifier)
  } catch {
    return null
  }
  const { vendorId, orderId } = parsed
  if (!vendorId || !orderId) {
    return null
  }
  const pdfUrl = queryParams.get('pdfUrl')
  if (!pdfUrl) {
    return null
  }
  const statusId = queryParams.get('statusId')
  const orderStatusName = statusId
    ? Utils.getConstantName(statusId, labOrderStatuses)
    : null
  const canComplete = orderStatusName === LabOrdersStatus.RESULTS_RECEIVED
  const hasResults = orderStatusName ? orderHasResults(orderStatusName) : false

  return {
    canComplete,
    hasResults,
    orderId,
    pdfUrl,
    vendorId,
  }
}
